import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { getYear, startOfMonth, setYear, setMonth, addMonths, subMonths, getDay, getDaysInMonth, addDays, subDays, eachDayOfInterval, isWeekend, isBefore, format, startOfDay, isAfter, isValid, isSameDay, getDate as getDate$1 } from 'date-fns';
import { useState, useEffect } from 'react';
import { defaultQuickRanges } from '../../constants.js';
import { ensureDate, chunk, formatDate, checkSelectRangeInYearRange, getTodayDate } from '../../utility.js';

var MIDDLE_ENDIAN_FORMAT = 'MM/DD/YYYY';
var BIG_ENDIAN_FORMAT = 'YYYY/MM/DD';
var REGEXS = _defineProperty(_defineProperty({}, BIG_ENDIAN_FORMAT, /^\d{4}\/\d{1,2}\/\d{1,2}$/), MIDDLE_ENDIAN_FORMAT, /^\d{1,2}\/\d{1,2}\/\d{4}$/);
var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
var todayYear = getYear(new Date());

/**
 * @param {string, Date} date
 * @returns {string} date in format MM/DD/YYYY
 */
var formatToMiddleEndian = function formatToMiddleEndian(date) {
  var dateObj = ensureDate(date);
  return dateObj ? format(dateObj, 'MM/dd/yyyy') : '';
};

/**
 * Check to see if one date is before another (or today if no date is passed)
 * @param {string | Date} date
 * @param {string} [before] string in format MM/DD/YYYY
 * @returns {boolean}
 */
function checkPastDate(date, before) {
  var pastDate = before ? ensureDate(before) : startOfDay(new Date());
  return isBefore(ensureDate(date), pastDate);
}

/**
 * check to see if one date is after another (or today if no date is passed)
 * @param {string | Date} date
 * @param {string} [after] string in format MM/DD/YYYY
 * @returns {boolean}
 */
function checkFutureDate(date, after) {
  var futureDate = after ? ensureDate(after) : startOfDay(new Date());
  return isAfter(ensureDate(date), futureDate);
}

/**
 * Checks if a date is a weekday
 * @param {Date|string} date
 * @returns {boolean}
 */
function checkWeekdayDate(date) {
  var dateObj = ensureDate(date);
  return isValid(dateObj) && !isWeekend(dateObj);
}

/**
 * Checks if a date is a weekend
 * @param {Date|string} date
 * @returns {boolean}
 */
function checkWeekendDate(date) {
  var dateObj = ensureDate(date);
  return isValid(dateObj) && isWeekend(dateObj);
}

/**
 * Checks if a date string is a valid date string and matches the format
 * @param {string} dateString
 * @param {string} [formatString = MIDDLE_ENDIAN_FORMAT
 * @returns {boolean}
 */
function checkValidDateFormat(dateString) {
  var formatString = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : MIDDLE_ENDIAN_FORMAT;
  var date = ensureDate(dateString);
  var regexFormat = REGEXS[formatString];
  return regexFormat.test(dateString) && isValid(date);
}

/**
 * Checks if two date strings are the same date
 * @param {string} date1
 * @param {string} date2
 * @returns {boolean}
 */
function checkSameDate(date1, date2) {
  // set dates to same format
  var formattedDate1 = formatDate(date1);
  var formattedDate2 = formatDate(date2);
  // then check for same date
  return formattedDate1 && formattedDate2 && checkValidDateFormat(formattedDate1) && checkValidDateFormat(formattedDate2) ? isSameDay(ensureDate(formattedDate1), ensureDate(formattedDate2)) : false;
}

/**
 * @param {ValueObject | string} date
 * @param {string[]} allowList  list of dates in format MM/DD/YYYY
 * @returns {boolean}
 */
function checkDateInList(date) {
  var allowList = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var dateString = (date === null || date === void 0 ? void 0 : date.value) || date;
  return allowList.some(function (allowDate) {
    return checkSameDate(dateString, allowDate);
  });
}

/**
 * Checks if a date string is today
 * @param {string} [date]
 * @returns {boolean}
 */
function checkToday(date) {
  return date ? checkSameDate(date, getTodayDate()) : false;
}

/**
 * Gets the first day of the month for a given date string
 * @param {string | null} [dateIn] - date in format YYYY/MM/DD or MM/DD/YYYY
 * @returns {string} - date in format MM/DD/YYYY
 */
function firstDayOfMonth(dateIn) {
  var date = dateIn ? new Date(dateIn) : new Date();
  var firstDay = startOfMonth(date);
  return formatToMiddleEndian(firstDay);
}

/**
 * Sets the date to the first day of the month by passing in a movement type
 * @param {string} dateIn
 * @param {string} [type] prev | next | month | year
 * @param {number} [monthOrYear]
 * @returns {string}
 */
function toActiveMonth(dateIn, type, monthOrYear) {
  var date = ensureDate(dateIn);
  switch (type) {
    case 'prev':
      date = subMonths(date, 1);
      break;
    case 'next':
      date = addMonths(date, 1);
      break;
    case 'month':
      date = setMonth(date, monthOrYear);
      break;
    case 'year':
      date = setYear(date, monthOrYear);
      break;
  }
  return formatToMiddleEndian(startOfMonth(date));
}

/**
 * Creates a date object from a date or date string
 * @param {Date|string} date
 * @param {DisabledDates} disableDates
 * @returns {{before: boolean, after: boolean, future: *, past: boolean, weekend: *, weekday: boolean, value: (string|string), dateNumber: (number|string)}}
 */
function toDateObj(date, disableDates) {
  return {
    after: checkFutureDate(date, disableDates === null || disableDates === void 0 ? void 0 : disableDates.after),
    before: checkPastDate(date, disableDates === null || disableDates === void 0 ? void 0 : disableDates.before),
    dateNumber: date ? getDate$1(ensureDate(date)) : '',
    future: checkFutureDate(date),
    past: checkPastDate(date),
    value: date ? ensureDate(date).toISOString() : '',
    weekday: checkWeekdayDate(date),
    weekend: checkWeekendDate(date)
  };
}

/**
 * Creates an array of dates for the month
 * @param {string} activeMonth
 * @param {DisabledDates} disableDates
 * @param {string} [format]
 * @returns {*[]}
 */
function toMonthArray(activeMonth, disableDates) {
  var date = ensureDate(activeMonth) || new Date();
  var firstDateOfMonth = startOfMonth(date);
  var dayNo = getDay(firstDateOfMonth); //momentObj.day()
  var monthLength = getDaysInMonth(firstDateOfMonth); //momentObj.daysInMonth()

  var arr = Array(dayNo).fill(1);
  for (var i = 0; i < monthLength; i++) {
    var _date = addDays(firstDateOfMonth, i);
    arr.push(toDateObj(_date, disableDates));
  }
  return chunk(arr, 7);
}

/**
 * Moves the date to the next or previous valid date and takes into account disabled dates
 * @param {string} dateString
 * @param {string} type
 * @param {DisabledDates} disableDates
 * @param {string} format
 * @returns {string}
 */
function handleDateTransition(dateString, type, disableDates, format) {
  var date = ensureDate(dateString);

  // Iterate by one until we land on the next valid date
  do {
    if (type === 'prev') {
      date = subDays(date, 1);
    } else if (type === 'next') {
      date = addDays(date, 1);
    }
  } while (disableDates !== null && disableDates !== void 0 && disableDates.only && checkDateInList(formatDate(date, MIDDLE_ENDIAN_FORMAT), disableDates.only) || disableDates !== null && disableDates !== void 0 && disableDates.except && !checkDateInList(formatDate(date, MIDDLE_ENDIAN_FORMAT), disableDates.except) || disableDates !== null && disableDates !== void 0 && disableDates.weekdays && checkWeekdayDate(date) || disableDates !== null && disableDates !== void 0 && disableDates.weekends && checkWeekendDate(date));
  return formatDate(date, format);
}
function getDate(str) {
  return (str.match('[-0-9/.\n]+') || []).pop() || '';
}

//Modified from https://usehooks.com/useKeyPress/
function useKeyPress(targetKey) {
  // State for keeping track of whether key is pressed
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    keyPressed = _useState2[0],
    setKeyPressed = _useState2[1];

  // Add event listeners
  useEffect(function () {
    // If pressed key is our target key then set to true
    function downHandler(_ref) {
      var keyCode = _ref.keyCode;
      if (keyCode === targetKey) {
        setKeyPressed(true);
      }
    }

    // If released key is our target key then set to false
    function upHandler(_ref2) {
      var keyCode = _ref2.keyCode;
      if (keyCode === targetKey) {
        setKeyPressed(false);
      }
    }
    window.addEventListener('keydown', downHandler);
    window.addEventListener('keyup', upHandler);
    // Remove event listeners on cleanup
    return function () {
      window.removeEventListener('keydown', downHandler);
      window.removeEventListener('keyup', upHandler);
    };
  }, [targetKey]);
  return keyPressed;
}

/**
 * Checks if the date is valid and in the correct format
 * @param {string} dateIn
 * @param {DisabledDates} disableDates
 * @param {string} format
 * @returns {{errorText: string, error: boolean}}
 */
var determineErrorState = function determineErrorState(_ref3) {
  var dateIn = _ref3.dateIn,
    disableDates = _ref3.disableDates,
    format = _ref3.format,
    dateRange = _ref3.dateRange;
  if (dateIn && !checkValidDateFormat(dateIn, format)) {
    return {
      error: true,
      errorText: "Please enter a valid date with format of ".concat(format)
    };
  } else if (disableDates && disableDates.past && checkPastDate(dateIn)) {
    return {
      error: true,
      errorText: 'Please select future date'
    };
  } else if (disableDates !== null && disableDates !== void 0 && disableDates.before && checkPastDate(dateIn, disableDates.before)) {
    return {
      error: true,
      errorText: "Please select future date from ".concat(disableDates.before)
    };
  } else if (disableDates && disableDates.future && checkFutureDate(dateIn)) {
    return {
      error: true,
      errorText: 'Please select past date'
    };
  } else if (disableDates !== null && disableDates !== void 0 && disableDates.after && checkFutureDate(dateIn, disableDates.after)) {
    return {
      error: true,
      errorText: "Please select past date from ".concat(disableDates.after)
    };
  } else if (disableDates && disableDates.weekdays && checkWeekdayDate(dateIn)) {
    return {
      error: true,
      errorText: 'Please select a weekend date'
    };
  } else if (disableDates && disableDates.weekends && checkWeekendDate(dateIn)) {
    return {
      error: true,
      errorText: 'Please select a weekday date'
    };
  } else if (dateIn && disableDates && disableDates.except && !checkDateInList(dateIn, disableDates.except)) {
    return {
      error: true,
      errorText: 'Please select valid date'
    };
  } else if (disableDates && disableDates.only && checkDateInList(dateIn, disableDates.only)) {
    return {
      error: true,
      errorText: 'Please select valid date'
    };
  } else if (dateRange) {
    if (!(dateIn !== null && dateIn !== void 0 && dateIn.length) > 0 && format) {
      return {
        error: true,
        errorText: "Please enter a valid date range with format of ".concat(format, " - ").concat(format)
      };
    }
    return {
      error: false,
      errorText: ''
    };
  } else {
    return {
      error: false,
      errorText: ''
    };
  }
};
var isDateDisabled = function isDateDisabled(date, disableDates) {
  return date && disableDates && (disableDates.past && date.past || disableDates.future && date.future || disableDates.weekdays && date.weekday || disableDates.weekends && date.weekend || disableDates.before && date.before || disableDates.after && date.after || disableDates.except && !checkDateInList(date, disableDates.except) || disableDates.only && checkDateInList(date, disableDates.only));
};
var isRangeDisabled = function isRangeDisabled(range, disableDates) {
  if (disableDates) {
    for (var _i = 0, _Object$keys = Object.keys(disableDates); _i < _Object$keys.length; _i++) {
      var option = _Object$keys[_i];
      switch (option) {
        case 'past':
          return isBefore(range.endDate, new Date()) || isBefore(range.startDate, new Date());
        case 'before':
          return isBefore(range.endDate, new Date(disableDates.before)) || isBefore(range.startDate, new Date(disableDates.before));
        case 'only':
          return eachDayOfInterval({
            end: range.endDate,
            start: range.startDate
          }).some(function (date) {
            return checkDateInList(date, disableDates.only);
          });
        case 'weekdays':
          return eachDayOfInterval({
            end: range.endDate,
            start: range.startDate
          }).some(function (date) {
            return !isWeekend(date);
          });
        case 'weekends':
          return eachDayOfInterval({
            end: range.endDate,
            start: range.startDate
          }).some(isWeekend);
        default:
          return false;
      }
    }
  }
};
var isRangeInYearRange = function isRangeInYearRange(range, yearRange) {
  if (yearRange) {
    if (checkSelectRangeInYearRange(yearRange, range.startDate) && checkSelectRangeInYearRange(yearRange, range.endDate)) {
      return true;
    }
    return false;
  }
  return false;
};
var getQuickSelectRange = function getQuickSelectRange(quickSelectRange) {
  if (Array.isArray(quickSelectRange)) {
    return quickSelectRange;
  }
  if (quickSelectRange === true) {
    return defaultQuickRanges;
  }
  return [];
};

export { MONTHS, checkDateInList, checkFutureDate, checkPastDate, checkSameDate, checkToday, checkValidDateFormat, checkWeekdayDate, checkWeekendDate, determineErrorState, firstDayOfMonth, getDate, getQuickSelectRange, handleDateTransition, isDateDisabled, isRangeDisabled, isRangeInYearRange, toActiveMonth, toDateObj, toMonthArray, todayYear, useKeyPress };
